import { useEffect, useState } from "react";

export type Theme = "dark" | "light" | "high-contrast";

const STORAGE_KEY = "kmitl:theme";
const CYCLE: Theme[] = ["dark", "light", "high-contrast"];

function readSavedTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "dark" || v === "light" || v === "high-contrast" ? (v as Theme) : null;
}

function systemTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => readSavedTheme() ?? systemTheme());

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.style.colorScheme = theme === "high-contrast" ? "dark" : theme;
  }, [theme]);

  // Follow system preference if the user hasn't explicitly chosen
  useEffect(() => {
    if (readSavedTheme()) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e: MediaQueryListEvent) => {
      if (readSavedTheme()) return;
      setThemeState(e.matches ? "light" : "dark");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const setTheme = (t: Theme) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // Safari private mode may throw
    }
    setThemeState(t);
  };

  return {
    theme,
    setTheme,
    toggle: () => {
      const next = CYCLE[(CYCLE.indexOf(theme) + 1) % CYCLE.length];
      setTheme(next);
    },
  };
}
